import SculptBase from '../tools/SculptBase';
import Utils from '../../misc/Utils';
import Smooth from './Smooth';
import Tablet from '../../misc/Tablet';
import MeshStatic from '../../mesh/meshStatic/MeshStatic';
import { vec3, mat3 } from 'gl-matrix';

class Masking extends SculptBase {

  constructor(main) {
    super(main);

    this._radius = 50;
    this._hardness = 0.25;
    this._intensity = 1.0;
    this._negative = true;
    this._culling = false;
    this._idAlpha = 'none';
    this._lockPosition = false;

    this._thickness = 0.0;
  }

  pushState() {
    // too lazy to add a pushStateMasking
    this._main.getStateManager().pushStateColorAndMasking(this.getMesh());
  }

  updateMeshBuffers() {
    var mesh = this.getMesh();
    if (mesh.isDynamic)
      mesh.updateBuffers();
    else
      mesh.updateMaskingBuffer();
  }

  stroke(picking) {
    var iVertsInRadius = picking.getPickedVertices();
    var intensity = this._intensity * Tablet.getPressureIntensity();

    // undo-redo
    this._main.getStateManager().pushVertices(iVertsInRadius);
    iVertsInRadius = this.dynamicTopology(picking);

    if (this._culling)
      iVertsInRadius = this.getFrontVertices(iVertsInRadius, picking.getEyeDirection());

    picking.updateAlpha(this._lockPosition);
    picking.setIdAlpha(this._idAlpha);
    this.paint(iVertsInRadius, picking.getIntersectionPoint(), picking.getLocalRadius2(), intensity, this._hardness, picking);
  }

  dynamicTopology(picking) {
    // no dynamic topo with masking
    return picking.getPickedVertices();
  }

  /** Paint color vertices */
  paint(iVerts, center, radiusSquared, intensity, hardness, picking) {
    var mesh = this.getMesh();
    var vAr = mesh.getVertices();
    var mAr = mesh.getMaskings();
    var radius = Math.sqrt(radiusSquared);
    var cx = center[0];
    var cy = center[1];
    var cz = center[2];
    var softness = 2 * (1 - hardness);
    var maskIntensity = this._negative ? -intensity : intensity;
    for (var i = 0, l = iVerts.length; i < l; ++i) {
      var iv = iVerts[i];
      var ind = iv * 3;
      var vx = vAr[ind];
      var vy = vAr[ind + 1];
      var vz = vAr[ind + 2];
      var dx = vx - cx;
      var dy = vy - cy;
      var dz = vz - cz;
      var dist = Math.sqrt(dx * dx + dy * dy + dz * dz) / radius;
      if (dist > 1) dist = 1.0;

      var fallOff = Math.pow(1.0 - dist, softness);
      fallOff *= maskIntensity * picking.getAlpha(vx, vy, vz);
      mAr[iv] = Math.min(Math.max(mAr[iv] + fallOff, 0.0), 1.0);
    }
  }

  updateAndRenderMask() {
    this.updateRender();
  }

  blur() {
    var mesh = this.getMesh();
    var iVerts = this.getMaskedVertices();
    if (iVerts.length === 0)
      return;
    iVerts = mesh.expandsVertices(iVerts, 1);

    this.pushState();
    this._main.getStateManager().pushVertices(iVerts);

    var mAr = mesh.getMaskings();
    var nbVerts = iVerts.length;
    var smoothVerts = new Float32Array(nbVerts);
    this.laplacianSmooth(iVerts, smoothVerts, mAr, 1);
    for (var i = 0; i < nbVerts; ++i) {
      mAr[iVerts[i]] = smoothVerts[i];
    }
    this.updateAndRenderMask();
  }

  sharpen() {
    var mesh = this.getMesh();
    var iVerts = this.getMaskedVertices();
    if (iVerts.length === 0)
      return;

    this.pushState();
    this._main.getStateManager().pushVertices(iVerts);

    var mAr = mesh.getMaskings();
    var nbVerts = iVerts.length;
    for (var i = 0; i < nbVerts; ++i) {
      var ivm = iVerts[i];
      var val = mAr[ivm];
      mAr[ivm] = (val > 0.5 ? Math.min(val + 0.1, 1.0) : Math.max(val - 1.0, 0.0));
    }
    this.updateAndRenderMask();
  }

  clear() {
    var mesh = this.getMesh();
    var iVerts = this.getMaskedVertices();
    if (iVerts.length === 0)
      return;

    this.pushState();
    this._main.getStateManager().pushVertices(iVerts);

    var mAr = mesh.getMaskings();
    for (var i = 0, nb = iVerts.length; i < nb; ++i) {
      mAr[iVerts[i]] = 1.0;
    }

    this.updateAndRenderMask();
  }

  invert(isState, meshState) {
    var mesh = meshState;
    if (!mesh) mesh = this.getMesh();
    if (!isState)
      this._main.getStateManager().pushStateCustom(this.invert.bind(this, true, mesh));

    var mAr = mesh.getMaskings();
    for (var i = 0, nb = mesh.getNbVertices(); i < nb; ++i) {
      mAr[i] = 1.0 - mAr[i];
    }

    this.updateAndRenderMask();
  }

  remapAndMirrorIndices(fAr, nbFaces, iVerts) {
    var nbVertices = this.getMesh().getNbVertices();
    var iTag = new Uint32Array(Utils.getMemory(nbVertices * 4), 0, nbVertices);
    var i = 0;
    var j = 0;
    var nbVerts = iVerts.length;
    for (i = 0; i < nbVerts; ++i)
      iTag[iVerts[i]] = i;

    var endFaces = nbFaces * 2;
    for (i = 0; i < endFaces; ++i) {
      j = i * 3;
      var offset = i < nbFaces ? 0 : nbVerts;
      fAr[j] = iTag[fAr[j]] + offset;
      fAr[j + 1] = iTag[fAr[j + 1]] + offset;
      fAr[j + 2] = iTag[fAr[j + 2]] + offset;
    }

    var end = fAr.length / 3;
    for (i = endFaces; i < end; ++i) {
      j = i * 3;
      fAr[j] = iTag[fAr[j]];
      fAr[j + 1] = iTag[fAr[j + 1]];
      fAr[j + 2] = iTag[fAr[j + 2]] + nbVerts;
    }
  }

  invertFaces(fAr) {
    for (var i = 0, nb = fAr.length / 3; i < nb; ++i) {
      var id = i * 3;
      var temp = fAr[id];
      fAr[id] = fAr[id + 2];
      fAr[id + 2] = temp;
    }
  }

  extractFaces(iFaces, iVerts, maskClamp) {
    var mesh = this.getMesh();
    var fAr = mesh.getFaces();
    var mAr = mesh.getMaskings();
    var eAr = mesh.getVerticesOnEdge();

    var noThick = this._thickness === 0;

    var nbFaces = iFaces.length;
    var nbNewFaces = new Uint32Array(Utils.getMemory(nbFaces * 4 * 3 * 3), 0, nbFaces * 3 * 3);
    var offsetFLink = noThick ? nbFaces : nbFaces * 2;
    for (var i = 0; i < nbFaces; ++i) {
      var idf = i * 3;
      var idOld = iFaces[i] * 3;
      var iv1 = nbNewFaces[idf] = fAr[idOld];
      var iv2 = nbNewFaces[idf + 1] = fAr[idOld + 1];
      var iv3 = nbNewFaces[idf + 2] = fAr[idOld + 2];
      if (noThick)
        continue;
      //
      // var b1 = mAr[iv1] >= maskClamp || eAr[iv1] >= 1;
      // var b2 = mAr[iv2] >= maskClamp || eAr[iv2] >= 1;
      // var b3 = mAr[iv3] >= maskClamp || eAr[iv3] >= 1;

      var b1 = eAr[iv1] >= 1;
      var b2 = eAr[iv2] >= 1;
      var b3 = eAr[iv3] >= 1;
      // create opposite face (layer), invert clockwise
      // tri =>
      // 1 2    3 2
      //  3      1

      idf += nbFaces * 3;
      nbNewFaces[idf] = iv3;
      nbNewFaces[idf + 1] = iv2;
      nbNewFaces[idf + 2] = iv1;

      // create bridges faces
      if (b2) {
        if (b1) {
          idf = 3 * (offsetFLink++);
          nbNewFaces[idf] = iv2;
          nbNewFaces[idf + 1] = nbNewFaces[idf + 2] = iv1;
        }
        if (b3) {
          idf = 3 * (offsetFLink++);
          nbNewFaces[idf] = iv3;
          nbNewFaces[idf + 1] = nbNewFaces[idf + 2] = iv2;
        }
      }
      if (b1 && b3) {
        idf = 3 * (offsetFLink++);
        nbNewFaces[idf] = iv1;
        nbNewFaces[idf + 1] = nbNewFaces[idf + 2] = iv3;
      }
    }

    var fArNew = new Uint32Array(nbNewFaces.subarray(0, offsetFLink * 3));
    this.remapAndMirrorIndices(fArNew, nbFaces, iVerts);
    if (this._thickness > 0)
      this.invertFaces(fArNew);
    return fArNew;
  }

  extractVertices(iVerts) {
    var mesh = this.getMesh();

    var vAr = mesh.getVertices();
    var nAr = mesh.getNormals();
    var mat = mesh.getMatrix();
    var nMat = mat3.normalFromMat4(mat3.create(), mat);
    var nbVerts = iVerts.length;
    var vArNew = new Float32Array(nbVerts * 2 * 3);
    var vTemp = [0.0, 0.0, 0.0];
    var nTemp = [0.0, 0.0, 0.0];
    var vOffset = nbVerts * 3;
    var thick = this._thickness;
    var eps = 0.01;
    if (thick < 0) eps = -eps;
    for (var i = 0; i < nbVerts; ++i) {
      var idv = i * 3;
      var idvOld = iVerts[i] * 3;
      vTemp[0] = vAr[idvOld];
      vTemp[1] = vAr[idvOld + 1];
      vTemp[2] = vAr[idvOld + 2];
      nTemp[0] = nAr[idvOld];
      nTemp[1] = nAr[idvOld + 1];
      nTemp[2] = nAr[idvOld + 2];
      vec3.transformMat3(nTemp, nTemp, nMat);
      vec3.normalize(nTemp, nTemp);

      vec3.transformMat4(vTemp, vTemp, mat);
      vec3.scaleAndAdd(vTemp, vTemp, nTemp, eps);
      vArNew[idv] = vTemp[0];
      vArNew[idv + 1] = vTemp[1];
      vArNew[idv + 2] = vTemp[2];

      vec3.scaleAndAdd(vTemp, vTemp, nTemp, thick);
      idv += vOffset;
      vArNew[idv] = vTemp[0];
      vArNew[idv + 1] = vTemp[1];
      vArNew[idv + 2] = vTemp[2];
    }
    return vArNew;
  }

  smoothBorder(mesh, iFaces) {
    var startBridge = iFaces.length * 2;
    var fBridge = new Uint32Array(mesh.getNbFaces() - startBridge);
    for (var i = 0, nbBridge = fBridge.length; i < nbBridge; ++i)
      fBridge[i] = startBridge + i;
    var vBridge = mesh.expandsVertices(mesh.getVerticesFromFaces(fBridge), 1);
    var smo = new Smooth();
    smo.setToolMesh(mesh);
    smo.smooth(vBridge, 1.0);
    smo.smooth(vBridge, 1.0);
    smo.smooth(vBridge, 1.0);
  }

  extract(mesh) {
    var iVerts = this.filterMaskedVertices(-Infinity, 0.5);
    if (iVerts.length === 0)
      return;

    return this._extract(mesh, iVerts, true);
  }

  extractRemaining(mesh) {
    var iVerts = this.filterMaskedVertices(0.5, +Infinity);
    if (iVerts.length === 0)
      return;

    return this._extract(mesh, iVerts);
  }

  _extract(mesh, iVerts, contain) {
    var iFaces = mesh.getFacesFromVertices(iVerts, contain);
    iVerts = mesh.getVerticesFromFaces(iFaces);

    var fArNew = this.extractFaces(iFaces, iVerts);
    var vArNew = this.extractVertices(iVerts);

    var newMesh = new MeshStatic();
    newMesh.setVertices(vArNew);
    newMesh.setFaces(fArNew);

    // we don't use newMesh.init because we want to smooth
    // the border (we want to avoid an update octree/normal/etc...)
    newMesh.initColorsAndMaskings();
    newMesh.allocateArrays();
    newMesh.initTopology();
    if (this._thickness !== 0.0)
      this.smoothBorder(newMesh, iFaces);
    newMesh.updateGeometry();

    return newMesh;
  }
}

export default Masking;
